import axios from 'axios';
import { useCallback, useRef, useState } from 'react';

// Runs entirely against Cloudflare's public speed-test edge (the same backend
// speed.cloudflare.com itself uses) instead of our own server. A test against our own
// infrastructure measured how fast we could talk to ourselves, which for a customer sitting on
// our own network (or reached via a path that bypasses the plan's bandwidth shaper) can read far
// above their real internet speed. Cloudflare's edge is only reachable over the customer's actual
// internet uplink, so it can't be shortcut the same way.
//
// A self-hosted comparison mode (backend/src/routes/speedtestLocal.js) was tried and removed
// 2026-09-08 — this server's own NIC negotiates at 100Mb/s and is shared with five other
// production services (ACS, RADIUS, WhatsApp gateway, etc.), so it under-reads even a mid-tier
// plan regardless of code correctness and can never validly measure a customer's real speed. See
// project_speedchecker_librespeed_exploration memory before resurrecting this on a different box.
//
// A separate axios instance (not the app's `api` client) — critical: the app's instance attaches
// our internal Bearer auth token to every request via an interceptor, and that must never go out
// to a third-party host.
const cf = axios.create({ baseURL: 'https://speed.cloudflare.com' });

const PING_ROUNDS = 5;
const MB = 1024 * 1024;
const CHUNK_BYTES = 20 * MB; // comfortably under Cloudflare's undocumented ~90-100MB per-request cap
const CONCURRENCY = 6; // parallel connections — higher-RTT paths (e.g. to Cloudflare's edge vs an
// on-network Ookla server) need more simultaneous streams to fill the same pipe in the same time

export const SPEEDTEST_SIZES = [
  { label: '100 MB (Quick)', bytes: 100 * MB },
  { label: '200 MB', bytes: 200 * MB },
  { label: '1 GB', bytes: 1024 * MB },
];
export const DEFAULT_SIZE_BYTES = SPEEDTEST_SIZES[0].bytes;

const timeoutFor = (bytes) => Math.max(30000, (bytes * 8) / (2 * 1e6) * 1000 + 15000); // assumes >=2 Mbps
const bytesToMbps = (bytes, ms) => (ms > 0 ? (bytes * 8) / (ms / 1000) / 1e6 : 0);

// Cloudflare's responses carry a Server-Timing header breaking the request into
// `cfSpeedEdge` (actual network round trip to their edge) and `cfSpeedWorker` (time spent
// executing the Worker that serves /__down — this can spike from ~30ms to 1000ms+ under load
// with nothing to do with the client's real network latency). Wall-clock timing the whole
// request conflates the two, which is why the ping this reported could read 5-10x higher than
// a raw ICMP-style ping (e.g. Ookla) to a nearby server. cfSpeedEdge alone is the comparable
// number. Falls back to null (caller uses wall-clock) if Cloudflare ever drops the header.
function edgeMsFrom(headers) {
  const serverTiming = headers?.['server-timing'];
  if (!serverTiming) return null;
  const match = /cfSpeedEdge;dur=([\d.]+)/.exec(serverTiming);
  return match ? parseFloat(match[1]) : null;
}

function randomBlob(size) {
  const buf = new Uint8Array(size);
  const CRYPTO_CHUNK = 65536; // crypto.getRandomValues' per-call limit
  for (let offset = 0; offset < size; offset += CRYPTO_CHUNK) {
    crypto.getRandomValues(buf.subarray(offset, Math.min(offset + CRYPTO_CHUNK, size)));
  }
  return new Blob([buf]);
}

function chunksFor(totalBytes) {
  const sizes = [];
  for (let remaining = totalBytes; remaining > 0; remaining -= CHUNK_BYTES) {
    sizes.push(Math.min(remaining, CHUNK_BYTES));
  }
  return sizes;
}

const WARMUP_BYTES = 5 * MB; // per parallel stream, untimed — needs to comfortably exceed the
// bandwidth-delay product of a higher-RTT path (e.g. Cloudflare's edge) so TCP has already ramped
// past slow-start before the timed transfer starts, not partway through it

// Opens `CONCURRENCY` connections and moves a little data over each before the real,
// timed transfer starts. Without this, whichever leg runs first (download) pays for TCP/TLS
// setup and slow-start ramp-up inside its measured time while the leg after it (upload) reuses
// already-warm connections — same total data, but download reads a few Mbps lower than upload
// for reasons that have nothing to do with actual bandwidth in either direction.
async function warmUp(requestFn) {
  await Promise.all(
    Array.from({ length: CONCURRENCY }, () => requestFn(WARMUP_BYTES, () => {}).catch(() => {}))
  );
}

const LOADED_PING_ROUNDS = 3;

// Fires a few ping probes while a download/upload is actively saturating the link, so the caller
// can compare against the idle ping — the increase is "bufferbloat": how much latency degrades
// once the pipe is full, same thing Ookla's ↓/↑ under-the-main-number figures show. Only ever
// called alongside runPooled (via Promise.all), never before/after it — measuring after the
// transfer finishes would just give back idle ping again.
async function measureLoadedPing(signal) {
  const times = [];
  for (let i = 0; i < LOADED_PING_ROUNDS; i++) {
    const t0 = performance.now();
    try {
      const res = await cf.get('/__down', { params: { bytes: 0 }, signal, timeout: 8000 });
      times.push(edgeMsFrom(res.headers) ?? (performance.now() - t0));
    } catch { /* link's busy — a failed probe just isn't counted */ }
  }
  return times.length ? Math.round(times.reduce((a, b) => a + b, 0) / times.length) : null;
}

// Fires `chunkSizes.length` requests (built by `requestFn`) through a fixed-size worker pool,
// reporting combined bytes-loaded-so-far/elapsed-so-far on every underlying progress tick so the
// caller can show one live speed across all of them.
async function runPooled(chunkSizes, requestFn, onTick) {
  const loaded = new Array(chunkSizes.length).fill(0);
  const start = performance.now();
  const tick = () => onTick(loaded.reduce((a, b) => a + b, 0), performance.now() - start);

  let next = 0;
  const worker = async () => {
    while (next < chunkSizes.length) {
      const i = next++;
      await requestFn(chunkSizes[i], (loadedBytes) => { loaded[i] = loadedBytes; tick(); });
      loaded[i] = chunkSizes[i];
      tick();
    }
  };
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, chunkSizes.length) }, worker));
  return performance.now() - start;
}

// Shared ping/download/upload runner behind the customer portal and technical dashboard speed
// test pages — same phases, same math, only the surrounding page layout differs between them.
export default function useSpeedTest() {
  const [phase, setPhase] = useState('idle'); // idle | ping | download | upload | done | error
  const [ping, setPing] = useState(null);
  const [jitter, setJitter] = useState(null);
  const [downloadMbps, setDownloadMbps] = useState(null);
  const [uploadMbps, setUploadMbps] = useState(null);
  const [liveMbps, setLiveMbps] = useState(null);
  const [downloadPingIncrease, setDownloadPingIncrease] = useState(null); // bufferbloat under download load
  const [uploadPingIncrease, setUploadPingIncrease] = useState(null); // bufferbloat under upload load
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const reset = useCallback(() => {
    setPhase('idle'); setPing(null); setJitter(null);
    setDownloadMbps(null); setUploadMbps(null); setLiveMbps(null); setError(null);
    setDownloadPingIncrease(null); setUploadPingIncrease(null);
  }, []);

  const run = useCallback(async (sizeBytes = DEFAULT_SIZE_BYTES) => {
    const controller = new AbortController();
    abortRef.current = controller;
    reset();
    const timeout = timeoutFor(Math.min(CHUNK_BYTES, sizeBytes)); // per-request, not per-whole-test

    try {
      // Ping — network round-trip to Cloudflare's edge, read from their Server-Timing header
      // (cfSpeedEdge) rather than timed client-side wall-clock, so Worker execution time
      // doesn't inflate the number (see edgeMsFrom above).
      setPhase('ping');
      const times = [];
      for (let i = 0; i < PING_ROUNDS; i++) {
        const t0 = performance.now();
        const res = await cf.get('/__down', { params: { bytes: 0 }, signal: controller.signal, timeout: 8000 });
        times.push(edgeMsFrom(res.headers) ?? (performance.now() - t0));
      }
      const idlePing = Math.round(Math.min(...times));
      setPing(idlePing);
      setJitter(Math.round(Math.max(...times) - Math.min(...times)));

      const downloadRequest = (bytes, onProgress) => cf.get('/__down', {
        params: { bytes },
        responseType: 'arraybuffer',
        signal: controller.signal,
        timeout,
        onDownloadProgress: (e) => onProgress(e.loaded),
      });
      const uploadRequest = (bytes, onProgress) => cf.post('/__up', randomBlob(bytes), {
        headers: { 'Content-Type': 'application/octet-stream' },
        signal: controller.signal,
        timeout,
        onUploadProgress: (e) => onProgress(e.loaded),
      });

      // Download
      setPhase('download');
      setLiveMbps(null);
      const chunks = chunksFor(sizeBytes);
      await warmUp(downloadRequest);
      const [dlElapsed, dlLoadedPing] = await Promise.all([
        runPooled(chunks, downloadRequest,
          (loaded, elapsed) => { if (elapsed > 0) setLiveMbps(bytesToMbps(loaded, elapsed)); }),
        measureLoadedPing(controller.signal),
      ]);
      setDownloadMbps(bytesToMbps(sizeBytes, dlElapsed));
      setDownloadPingIncrease(dlLoadedPing != null ? Math.max(0, dlLoadedPing - idlePing) : null);

      // Upload
      setPhase('upload');
      setLiveMbps(null);
      await warmUp(uploadRequest);
      const [ulElapsed, ulLoadedPing] = await Promise.all([
        runPooled(chunks, uploadRequest,
          (loaded, elapsed) => { if (elapsed > 0) setLiveMbps(bytesToMbps(loaded, elapsed)); }),
        measureLoadedPing(controller.signal),
      ]);
      setUploadMbps(bytesToMbps(sizeBytes, ulElapsed));
      setUploadPingIncrease(ulLoadedPing != null ? Math.max(0, ulLoadedPing - idlePing) : null);

      setLiveMbps(null);
      setPhase('done');
    } catch (err) {
      if (controller.signal.aborted) { setPhase('idle'); return; }
      setError('Speed test failed — check your connection and try again');
      setPhase('error');
    }
  }, [reset]);

  const cancel = useCallback(() => { abortRef.current?.abort(); }, []);

  return {
    phase, ping, jitter, downloadMbps, uploadMbps, liveMbps,
    downloadPingIncrease, uploadPingIncrease, error, run, cancel, reset,
  };
}
